import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Input, Spin, Card, Select, Tooltip, notification } from "antd";
import { SearchOutlined, LockOutlined, SyncOutlined, ReloadOutlined } from "@ant-design/icons";
import styles from "./style.module.scss";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import BoardCard from "components/Dashboard/BoardCard";
import DashboardHeader from "components/Dashboard/Header";
import DashboardPageChrome from "components/Dashboard/DashboardPageChrome";
import NestedTabs from "components/NestedTabs";
import CustomSunburstChart from "components/Charts/CustomSunburstChart";
import SelectedTopicInfo from "components/Dashboard/SelectedTopicInfo";
import { apiRequest } from "util/services";
import * as actions from "redux/Dashboard/actions.js";
import { scrollToElement } from "util/common";
import EmergentCandidatesPanel from "./EmergentCandidatesPanel";
import {
  getCombinedViewDefaultRange,
  resolveCombinedViewDateRange,
} from "./combinedViewDateRange";

const PENDING_RESTRICTED_INDUSTRIES = [
  { id: null, name: "telecom", allowed: false },
  { id: null, name: "retail", allowed: false },
];

const CombinedView = ({
  shared,
  currentDashboard,
  authenticated,
  user,
  match,
  currentDashboardHash,
}) => {
  const DashboardReducer = useSelector((state) => state.DashboardReducer);
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const { user: reduxUser } = DashboardReducer;
  const params = new URLSearchParams(window.location.search);
  const { id } = useParams();
  const [selectedTopicData, setSelectedTopicData] = useState(null);
  const dispatch = useDispatch();

  const initialState = {
    currentDashboard: shared
      ? currentDashboard
      : match.params.id || DashboardReducer.dashboard?.ID,
  };
  const [localState, setState] = useState(initialState);
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [queryFilters, setQueryFilters] = useState({});
  const { search } = useLocation();

  const [data, setData] = useState({ original: null, filtered: null });
  const [isLoading, setIsLoading] = useState(true);
  const [topicsLoadError, setTopicsLoadError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [changeDateRange, setChangeDateRange] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState({ index: -1 });
  const [showDeepDive, setShowDeepDive] = useState(false);
  const [activeTopic, setActiveTopic] = useState({});
  const [disableDateRange, setDisableDateRange] = useState([true, true]);
  const [topicChanged, setTopicChanged] = useState(false);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);

  // Fetch dashboard info and set in Redux
  useEffect(() => {
    if (id) {
      apiRequest("dashboards/v2/get", "POST", { dashboard_id: Number(id) }, true).then((result) => {
        if (result && !result.error) {
          dispatch({ type: actions.SET_Dashboard, payload: { ...result, loading: false } });
          setDashboardLoaded(true);
        } else {
          notification.error({
            message: result?.error || "Unable to get dashboard info",
          });
        }
      });
    }
  }, [id, dispatch]);

  useEffect(() => {
    if(location.state && location.state?.searchInsight && location.state?.selected_topic){
      const arg = location.state.selected_topic;
      if(arg.topic_type === 'ai'){
        arg.type = 1;
      }else arg.type = 2;
      setShowDeepDive(true);
      setSelectedTopic(location.state.selected_topic)
    }
  }, [location.state]);

  useEffect(() => {
    if (params) {
      const values = Array.from(params);
      const queryObj = values.reduce((acc, [key, val]) => {
        if (
          key === "keyword" ||
          key === "pivotFilter" ||
          key === "top_topics" ||
          key === "level1OfSelected" ||
          key === "level2OfSelected" ||
          key === "selected_topic" ||
          key === "active_topic_data" ||
          key === "decisionFilter" ||
          key === "child_others" ||
          key === "parent_others" ||
          key === "hotterm"
        ) {
          acc[key] = JSON.parse(val);
        } else {
          acc[key] = val;
        }
        return acc;
      }, {});
      setQueryFilters(queryObj);
    }
  }, [history, search]);

  useEffect(() => {
    if (showDeepDive && selectedTopic?.id && DashboardReducer?.dashboard?.ID) {
      getSelectedTopicData(selectedTopic?.id, selectedTopic)
    }
  }, [showDeepDive, selectedTopic?.id, DashboardReducer?.dashboard?.ID]);

  // Utility to ensure topic object has only minimal fields for URL
  const normalizeTopic = (topic) => {
    if (!topic) return topic;
    if (typeof topic !== 'object') return topic;
    const { index, backgroundColor, label, id, name, type, parent_id, TopicId } = topic;
    const resolvedId = id ?? TopicId ?? undefined;
    return {
      index: index ?? -1,
      backgroundColor: backgroundColor ?? undefined,
      label: label ?? name ?? '',
      id: resolvedId,
      type: type ?? 1,
      parent_id: parent_id ?? undefined
    };
  };

  const getSelectedTopicData = async (topicId, newTopic, activeTopicData) => {
    if (topicId === selectedTopic) {
      setState({
        ...localState,
        infoLoading: false,
      });
      setSelectedTopicData();
      params.delete("selected_topic");
      params.delete("active_topic_data");
      [
        "top_topics",
        "topics_operator",
        "hotterm",
        "nps",
        "keyword",
        "pivotFilter",
        "pivots_operator",
        "level1OfSelected",
        "level2OfSelected",
        "decisionFilter",
        "cursor_next",
        "cursor_curr",
        "cursor_before",
        "savedSearchID",
        "child_others",
        "parent_others",
      ].forEach((item) => params.delete(item));
      history.push({ search: params.toString() });
      setSelectedTopic({ index: -1 });
      return;
    }
    
    // Clear URL parameters and reset search state like other components do
    for (let l = 1; l >= 1; l++) {
      if (params.has(`level${l}Selection`)) {
        params.delete(`level${l}Selection`);
        dispatch({
          type: actions.UPDATE_SEARCH_STATE,
          payload: {
            [`selectedL${l}Topic`]: null,
          },
        });
      } else {
        break;
      }
    }
    [
      "top_topics",
      "topics_operator",
      "hotterm",
      "nps",
      "keyword",
      "pivotFilter",
      "pivots_operator",
      "level1OfSelected",
      "level2OfSelected",
      "decisionFilter",
      "cursor_next",
      "cursor_curr",
      "cursor_before",
      "savedSearchID",
      "child_others",
      "parent_others",
    ].forEach((item) => {
      if (params.has(item)) {
        params.delete(item);
      }
    });
    history.push({ search: params.toString() });
    
    // Reset search state like other components do
    dispatch({
      type: actions.UPDATE_SEARCH_STATE,
      payload: {
        chosenKeywords: [],
        chosenFetchType: "topic",
        chosenHotTerm: "",
        chosenRestaurantName: "",
        chosenNPS: "",
        chosenTopTopic: [],
      },
    });
    
    scrollToElement("SelectedTopicInfo");
    setState({ ...localState, infoLoading: true });
    const topicRange = resolveCombinedViewDateRange(
      params.get("since"),
      params.get("until"),
      DashboardReducer
    );
    const result = await apiRequest(
      "dashboards/v2/get/topics/selected_topic",
      "POST",
      {
        dashboard_id: parseInt(id),
        topic_id: topicId,
        topic_type: selectedTopic?.type == 1 ? "ai" : "custom",
        n: 5,
        since: topicRange.since,
        until: topicRange.until,
      },
      true
    );
    if (!result.error) {
      let data = { ...result, id: topicId, infoLoading: false };
      if (typeof data.TopWords === "string") {
        data.TopWords = data.TopWords.replace(/[\[|\]|\']/g, "").split(", ");
      }
      setSelectedTopicData(data);
      // Ensure newTopic has only minimal fields for the URL
      const normalizedTopic = normalizeTopic({ ...newTopic, id: topicId });
      updateQParams("selected_topic", JSON.stringify(normalizedTopic));
      // Always set topics_type based on newTopic.type
      if (newTopic && (newTopic.type === 1 || newTopic.type === "1")) {
        updateQParams("topics_type", "ai");
      } else if (newTopic && (newTopic.type === 2 || newTopic.type === "2")) {
        updateQParams("topics_type", "custom");
      } else {
        updateQParams("topics_type", "custom"); // fallback
      }
      // Set selectedTopic to the full API response (with all metadata)
      setSelectedTopic(normalizedTopic);
      dispatch({
        type: actions.UPDATE_Dashboard,
        payload: {
          showNewReviewsLabel: false,
        },
      });
      setState({ ...localState, blur: false, infoLoading: false });
      scrollToElement("SelectedTopicInfo");
      if (
        DashboardReducer.insightBasedSearch &&
        location?.state?.selected_topic === topicId
      ) {
        dispatch({
          type: actions.UPDATE_SEARCH_STATE,
          payload: DashboardReducer.insightBasedSearch,
        });
        dispatch({
          type: actions.UPDATE_Dashboard,
          payload: {
            showNewReviewsLabel: true,
          },
        });
      }
    } else {
      notification.error({
        message: result.error,
      });
      setState({ ...localState, blur: true, infoLoading: false });
    }
  };

  const closeSelectedTopic = () => {
    if (params.get("insightTopicSearch")) {
      updateQParams("insightTopicClosed", "true");
    }
    [
      "sentiment",
      "intent",
      "rating",
      "top_topics",
      "topics_operator",
      "platform_id",
      "match_date",
      "hotterm",
      "nps",
      "keyword",
      "pivotFilter",
      "pivots_operator",
      "level1Selection",
      "level2Selection",
      "level1OfSelected",
      "level2OfSelected",
      "selected_topic",
      "insightTopicSearch",
      "decisionFilter",
      "savedSearchID",
      "child_others",
      "parent_others",
    ].map((item) => params.delete(item));
    history.push({ search: params.toString() });
    setSelectedTopic({ index: -1 });
    setSelectedTopicData();
    setActiveTopic({});
  };
    // Parse query params once per location.search
    const queryParams = useMemo(() => {
      const p = new URLSearchParams(location.search);
      const obj = {};
      for (const [key, value] of p.entries()) {
        // parse JSON for specific keys, otherwise string
        if (
          [
            "keyword",
            "pivotFilter",
            "top_topics",
            "level1OfSelected",
            "level2OfSelected",
            "selected_topic",
            "decisionFilter",
            "active_topic_data",
            "child_others",
            "parent_others",
            "hotterm",
          ].includes(key)
        ) {
          try {
            obj[key] = JSON.parse(value);
          } catch {
            obj[key] = value;
          }
        } else {
          obj[key] = value;
        }
      }
      return obj;
    }, [location.search]);
  
  const updateQParams = (attr, val) => {
    params.delete(attr);
    params.append(attr, val);
    history.push({ search: params.toString() });
  };

  const { since, until } = useMemo(
    () =>
      resolveCombinedViewDateRange(
        queryParams.since,
        queryParams.until,
        DashboardReducer
      ),
    [
      queryParams.since,
      queryParams.until,
      DashboardReducer?.dashboard?.RefreshRate,
      DashboardReducer?.dashboard_end_date,
      DashboardReducer?.end_date,
    ]
  );

  useEffect(() => {
    if (!id) return;
    const p = new URLSearchParams(location.search);
    const currentSince = p.get("since");
    const currentUntil = p.get("until");

    if (!currentSince || !currentUntil) {
      const defaults = getCombinedViewDefaultRange(DashboardReducer);
      p.set("since", defaults.since);
      p.set("until", defaults.until);
      p.set("daysRange", defaults.daysRange);
      history.replace({ search: p.toString() });
      return;
    }

    const resolved = resolveCombinedViewDateRange(
      currentSince,
      currentUntil,
      DashboardReducer
    );
    if (
      resolved.clamped &&
      (currentSince !== resolved.since || currentUntil !== resolved.until)
    ) {
      p.set("since", resolved.since);
      p.set("until", resolved.until);
      const rangeKey = p.get("daysRange");
      if (rangeKey === "allTime" || rangeKey === "last14d" || rangeKey === "last30d" || rangeKey === "last90d") {
        p.set("daysRange", "last7d");
      }
      history.replace({ search: p.toString() });
    }
  }, [id, location.search, DashboardReducer, history]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        dashboard_id: Number(id),
        since: since || "",
        until: until || "",
      };
      const result = await apiRequest(
        "dashboards/sunburst",
        "POST",
        payload,
        true
      );
      if (result?.cancel || result?.error) {
        setTopicsLoadError(true);
        return;
      }
      if (result?.success) {
        setData({ original: result.success, filtered: result.success });
        setTopicsLoadError(false);
      } else {
        setTopicsLoadError(true);
      }
    } catch (err) {
      console.error("Failed to load sunburst data", err);
      setTopicsLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [id, since, until]);

  // Load on mount or when id/since/until change
  useEffect(() => {
    if (!id) {
      history.replace("/console/myDashboards");
      return;
    }
    fetchData();
  }, [id, since, until, history, fetchData]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim() || !data.original) {
      setData((d) => ({ ...d, filtered: d.original }));
      return;
    }
    const lower = value.toLowerCase();
    // Recursively filter a node
    const filterNode = (node) => {
      if (!node) return null;
      const nameMatch = node.name?.toLowerCase().includes(lower);
      let children = node.children?.map(filterNode).filter(Boolean);
      if (nameMatch || (children && children?.length)) {
        return { ...node, children: children?.length ? children : undefined };
      }
      return null;
    };
    setData((d) => ({ ...d, filtered: filterNode(d.original) }));
  };

  const toggleSearchBox = () => setShowSearchBox((s) => !s);

  // Update URL without reloading component
  const updateQParam = (key, val) => {
    const p = new URLSearchParams(location.search);
    if (val == null) p.delete(key);
    else p.set(key, val);
    history.push({ pathname: location.pathname, search: p.toString() });
  };

  useEffect(() => {
    if (topicChanged) {
      setShowDeepDive(false);
      fetchData();
      setTopicChanged(false);
    }
  }, [topicChanged, fetchData]);

  const [viewMode, setViewMode] = useState(() => {
    const v = new URLSearchParams(window.location.search).get("topics_view");
    return ["doughnut", "list", "hierarchical"].includes(v) ? v : "doughnut";
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [applyingIndustry, setApplyingIndustry] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [loadingIndustries, setLoadingIndustries] = useState(false);

  const setViewModeAndUrl = (mode) => {
    setViewMode(mode);
    setSelectedIds([]);
    updateQParam("topics_view", mode);
  };

  const collectTopicNodes = useCallback((node, acc = [], depth = 0) => {
    if (!node) return acc;
    const id = node.id ?? node.TopicId;
    const isRoot = id === "root" || id === 0 || id === "0";
    if (!isRoot && id != null && Number(id) > 0) {
      acc.push({
        id: Number(id),
        name: node.name || node.label || node.Label || `Topic ${id}`,
        type: node.type,
        depth,
        hasChildren: Array.isArray(node.children) && node.children.length > 0,
        children: node.children || [],
        is_genai: node.is_genai || node.type === 3 || node.type === "ai",
      });
    }
    (node.children || []).forEach((child) =>
      collectTopicNodes(child, acc, isRoot ? 0 : depth + 1)
    );
    return acc;
  }, []);

  const flatTopics = useMemo(
    () => collectTopicNodes(data.filtered || data.original),
    [data.filtered, data.original, collectTopicNodes]
  );

  const hasTopics = flatTopics.length > 0;
  const dashboardStatus = DashboardReducer.dashboard?.Status;
  // Monolith apply sets Status "In Progress"; legacy leftover may still be
  // "Custom Topics Running". Journey also surfaces monolith_steps while running.
  const topicsApplyingPending =
    dashboardStatus === "In Progress" ||
    dashboardStatus === "Custom Topics Running" ||
    dashboardStatus === "Deleting Custom Topic";

  const allSelected =
    hasTopics && selectedIds.length > 0 && selectedIds.length === flatTopics.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(flatTopics.map((t) => t.id));
    }
  };

  const toggleSelectOne = (topicId) => {
    setSelectedIds((prev) =>
      prev.includes(topicId)
        ? prev.filter((x) => x !== topicId)
        : [...prev, topicId]
    );
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;
    setDeleting(true);
    const result = await apiRequest(
      "dashboards/v2/delete/custom_topic",
      "POST",
      {
        dashboard_id: Number(id),
        topic_ids: selectedIds,
        all: false,
      },
      true
    );
    setDeleting(false);
    if (result?.error) {
      notification.error({ message: result.error });
      return;
    }
    notification.success({
      message: t("dashboard.topics_deleted", {
        defaultValue: "Topics removed from this dashboard",
      }),
    });
    setSelectedIds([]);
    setShowDeepDive(false);
    fetchData();
  };

  useEffect(() => {
    let cancelled = false;
    const loadIndustries = async () => {
      setLoadingIndustries(true);
      // Same source as NewDashboard step 1 — includes allowed + not-applied (restricted).
      const response = await apiRequest("dashboards/models", "POST", null, true);
      if (cancelled) return;
      setLoadingIndustries(false);
      if (!response?.models?.length) return;

      const apiNames = new Set(
        response.models.map((item) => (item.name || "").toLowerCase())
      );
      const pending = PENDING_RESTRICTED_INDUSTRIES.filter(
        (item) => !apiNames.has(item.name.toLowerCase())
      );
      // "Others" is for dashboard creation only — not a Topic Library taxonomy.
      const merged = [...response.models, ...pending].filter(
        (item) => item.id !== -1 && item.name !== "Others"
      );
      setIndustries(merged);

      setSelectedIndustry((prev) => {
        if (prev) return prev;
        const defaultIndustry =
          merged.find(
            (item) => item.id === response?.default?.id && item.allowed
          ) || merged.find((item) => item.allowed);
        return defaultIndustry?.name || null;
      });
    };
    if (flatTopics.length === 0 && !isLoading) {
      loadIndustries();
    }
    return () => {
      cancelled = true;
    };
  }, [flatTopics.length, isLoading]);

  const industrySelectOptions = useMemo(() => {
    const byName = (a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      });
    const available = industries
      .filter((item) => item.allowed !== false)
      .sort(byName);
    const restricted = industries
      .filter((item) => item.allowed === false)
      .sort(byName);
    const toOption = (industry) => ({
      value: industry.name,
      label: industry.name,
      title: industry.name,
      allowed: industry.allowed !== false,
      disabled: industry.allowed === false,
      className:
        industry.allowed === false ? styles.industryOptionRestricted : undefined,
    });
    const groups = [];
    if (available.length > 0) {
      groups.push({
        label: t("new_dashboard.69").toUpperCase(),
        options: available.map(toOption),
      });
    }
    if (restricted.length > 0) {
      groups.push({
        label: t("new_dashboard.70").toUpperCase(),
        options: restricted.map(toOption),
      });
    }
    return groups;
  }, [industries, t]);

  const renderIndustryOption = (option) => {
    const isRestricted = option.data?.allowed === false || option.data?.disabled;
    const label = option.label ?? option.data?.label ?? "";
    const row = (
      <div
        className={`${styles.industryOption} ${
          isRestricted ? styles.industryOptionRestricted : ""
        }`}
      >
        <span className={styles.industryOptionLabel}>{label}</span>
        {isRestricted && (
          <LockOutlined className={styles.industryLockIcon} aria-hidden="true" />
        )}
      </div>
    );
    if (!isRestricted) return row;
    return (
      <Tooltip title={t("new_dashboard.71")} placement="right">
        <span className={styles.industryOptionTooltipWrap}>{row}</span>
      </Tooltip>
    );
  };

  const refreshDashboardStatus = useCallback(async () => {
    const result = await apiRequest(
      "dashboards/v2/get",
      "POST",
      { dashboard_id: Number(id) },
      true
    );
    if (result && !result.error) {
      dispatch({
        type: actions.SET_Dashboard,
        payload: { ...result, loading: false },
      });
    }
  }, [id, dispatch]);

  const handleApplyIndustry = async () => {
    if (!selectedIndustry) {
      notification.warning({
        message: t("dashboard.topics_select_industry", {
          defaultValue: "Please select an industry first",
        }),
      });
      return;
    }
    setApplyingIndustry(true);
    const result = await apiRequest(
      "library/industry_topics/apply",
      "POST",
      { dashboard_id: Number(id), industry: selectedIndustry },
      true
    );
    setApplyingIndustry(false);
    if (result?.error) {
      notification.error({ message: result.error });
      return;
    }
    const created = result?.success?.created ?? 0;
    const industry = result?.success?.industry || selectedIndustry;
    notification.success({
      message: t("dashboard.topics_industry_applied", {
        defaultValue: "{{count}} {{industry}} topics applied. Pipeline started.",
        count: created,
        industry,
      }),
    });
    // Status → "Custom Topics Running" unlocks Journey in NestedTabs.
    await refreshDashboardStatus();
    // Pipeline runs async — refresh shortly so new topics appear.
    setTimeout(() => fetchData(), 2500);
  };

  // While industry topics are applying, poll until sunburst has data or status settles.
  useEffect(() => {
    if (!topicsApplyingPending || hasTopics) return undefined;
    const timer = setInterval(() => {
      refreshDashboardStatus();
      fetchData();
    }, 15000);
    return () => clearInterval(timer);
  }, [topicsApplyingPending, hasTopics, refreshDashboardStatus, fetchData]);

  const renderTopicRow = (topic, { showDepth = false } = {}) => (
    <div
      key={topic.id}
      className={styles.topicRow}
      style={showDepth ? { paddingLeft: 12 + topic.depth * 20 } : undefined}
    >
      <input
        type="checkbox"
        className={styles.topicCheck}
        checked={selectedIds.includes(topic.id)}
        onChange={() => toggleSelectOne(topic.id)}
      />
      <button
        type="button"
        className={styles.topicNameBtn}
        onClick={() => {
          const normalized = normalizeTopic({
            id: topic.id,
            label: topic.name,
            type: topic.type === "ai" || topic.type === 1 ? 1 : 2,
          });
          updateQParams("selected_topic", JSON.stringify(normalized));
          setSelectedTopic(normalized);
          setShowDeepDive(true);
        }}
      >
        {topic.name}
      </button>
      {(topic.is_genai || topic.type === 3) && (
        <span className={styles.genaiBadge}>{t("topic_library.genai_badge")}</span>
      )}
    </div>
  );

  const renderLoadError = () => (
    <div className={styles.emptyState}>
      <p className={styles.emptyTitle}>
        {t("dashboard.topics_load_error_title", {
          defaultValue: "Veriler yüklenemedi",
        })}
      </p>
      <p className={styles.emptySubtitle}>
        {t("dashboard.topics_load_error_subtitle", {
          defaultValue:
            "Konu listesi şu an alınamadı. Bağlantınızı kontrol edip tekrar deneyin.",
        })}
      </p>
      <button type="button" className={styles.applyBtn} onClick={() => fetchData()}>
        <ReloadOutlined />{" "}
        {t("dashboard.topics_load_retry", { defaultValue: "Tekrar dene" })}
      </button>
    </div>
  );

  const renderEmptyState = () => {
    if (topicsApplyingPending) {
      return (
        <div className={styles.emptyState}>
          <SyncOutlined spin className={styles.inProgressIcon} />
          <p className={styles.emptyTitle}>
            {t("dashboard.topics_applying_title", {
              defaultValue: "Topics are being applied",
            })}
          </p>
          <p className={styles.emptySubtitle}>
            {t("dashboard.topics_applying_subtitle", {
              defaultValue:
                "Industry topics are already in progress for this dashboard. You can follow the status on the Journey page.",
            })}
          </p>
          <button
            type="button"
            className={styles.applyBtn}
            onClick={() => history.push(`/console/journey/${id}`)}
          >
            {t("dashboard.topics_view_journey", {
              defaultValue: "View Journey progress",
            })}
          </button>
        </div>
      );
    }

    return (
    <div className={styles.emptyState}>
      <p className={styles.emptyTitle}>
        {t("dashboard.topics_empty_title", {
          defaultValue: "No topics on this dashboard yet",
        })}
      </p>
      <p className={styles.emptySubtitle}>
        {t("dashboard.topics_empty_subtitle", {
          defaultValue:
            "Apply your Topic Library industry taxonomy to classify reviews on this dashboard. Topic Library itself is not modified.",
        })}
      </p>
      <div className={styles.applyControls}>
        <Select
          className={styles.industrySelect}
          placeholder={t("dashboard.topics_select_industry", {
            defaultValue: "Select industry",
          })}
          loading={loadingIndustries}
          value={selectedIndustry}
          onChange={(value) => {
            const selected = industries.find((item) => item.name === value);
            if (!selected || selected.allowed === false) return;
            setSelectedIndustry(value);
          }}
          options={industrySelectOptions}
          optionRender={renderIndustryOption}
          showSearch
          optionFilterProp="title"
          filterOption={(input, option) => {
            const label = (option?.label ?? option?.title ?? "").toString();
            return label.toLowerCase().includes(input.toLowerCase());
          }}
          popupClassName={styles.industryDropdown}
          listHeight={320}
          disabled={applyingIndustry}
        />
        <button
          type="button"
          className={styles.applyBtn}
          disabled={applyingIndustry || !selectedIndustry}
          onClick={handleApplyIndustry}
        >
          {applyingIndustry
            ? t("dashboard.topics_applying", { defaultValue: "Applying…" })
            : t("dashboard.topics_apply_industry", {
                defaultValue: "Apply Topic Library industry to this dashboard",
              })}
        </button>
      </div>
    </div>
    );
  };

  const emergentReviewMode = params.get("emergent") === "review";

  return (
    <div className={styles.combinedView}>
      <DashboardPageChrome>
        <DashboardHeader
          shared={shared}
          topicsType="combined-view"
          user={reduxUser}
          setChangeDateRange={setChangeDateRange}
          onDateRangeChange={(newSince, newUntil) => {
            updateQParam("since", newSince);
            updateQParam("until", newUntil);
          }}
          dashboardId={id}
        />
        <NestedTabs />
      </DashboardPageChrome>
      <EmergentCandidatesPanel
        dashboardId={Number(id)}
        autoExpand={emergentReviewMode}
      />
      <BoardCard
        title={t("dashboard.combined_view")}
        extra={
          <div className={styles.actionBtnBlock}>
            <div className={styles.viewToggle}>
              <button
                type="button"
                className={`${styles.viewBtn} ${
                  viewMode === "doughnut" ? styles.viewBtnActive : ""
                }`}
                onClick={() => setViewModeAndUrl("doughnut")}
              >
                {t("dashboard.topics_view_doughnut", { defaultValue: "Doughnut" })}
              </button>
              <button
                type="button"
                className={`${styles.viewBtn} ${
                  viewMode === "list" ? styles.viewBtnActive : ""
                }`}
                onClick={() => setViewModeAndUrl("list")}
              >
                {t("dashboard.topics_view_list", { defaultValue: "List" })}
              </button>
              <button
                type="button"
                className={`${styles.viewBtn} ${
                  viewMode === "hierarchical" ? styles.viewBtnActive : ""
                }`}
                onClick={() => setViewModeAndUrl("hierarchical")}
              >
                {t("dashboard.topics_view_hierarchical", {
                  defaultValue: "Hierarchical",
                })}
              </button>
            </div>
            {showSearchBox && (
              <Input
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                prefix={<SearchOutlined />}
                className={styles.searchInput}
              />
            )}
            <button className={styles.search} onClick={toggleSearchBox}>
              <SearchOutlined />
            </button>
          </div>
        }
      >
        <div className={styles.chartContainer}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <Spin size="large" />
            </div>
          ) : topicsLoadError && !hasTopics ? (
            renderLoadError()
          ) : !hasTopics ? (
            renderEmptyState()
          ) : viewMode === "doughnut" ? (
            <CustomSunburstChart
              data={data.filtered}
              width={900}
              height={900}
              theme={theme}
              setSelectedTopic={(topic) => {
                const normalized = normalizeTopic(topic);
                if (!normalized?.id) return;
                updateQParams("selected_topic", JSON.stringify(normalized));
                setSelectedTopic(normalized);
                setShowDeepDive(true);
              }}
              setShowDeepDive={setShowDeepDive}
            />
          ) : (
            <div className={styles.listPanel}>
              <div className={styles.listToolbar}>
                <label className={styles.selectAllLabel}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                  {t("dashboard.topics_select_all", {
                    defaultValue: "Select all",
                  })}{" "}
                  ({flatTopics.length})
                </label>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  disabled={!selectedIds.length || deleting}
                  onClick={handleDeleteSelected}
                >
                  {deleting
                    ? t("dashboard.topics_deleting", { defaultValue: "Deleting…" })
                    : t("dashboard.topics_delete_selected", {
                        defaultValue: "Delete from dashboard ({{count}})",
                        count: selectedIds.length,
                      })}
                </button>
              </div>
              <div className={styles.topicList}>
                {viewMode === "list"
                  ? flatTopics.map((topic) => renderTopicRow(topic))
                  : flatTopics.map((topic) =>
                      renderTopicRow(topic, { showDepth: true })
                    )}
              </div>
            </div>
          )}
        </div>
      </BoardCard>

      {showDeepDive && selectedTopic && (
        <SelectedTopicInfo
          shared={shared}
          selectedTopicData={selectedTopicData}
          setSelectedTopic={setSelectedTopic}
          selectedTopic={selectedTopic}
          closeSelectedTopic={closeSelectedTopic}
          user={user}
          topicsType={selectedTopic?.type == 1 ? "ai" : "custom"}
          activeTopic={activeTopic}
          DashboardId={parseInt(id, 10)}
          currentDashboardHash={currentDashboardHash}
          setDisableDateRange={setDisableDateRange}
          topicChanged={topicChanged}
          setTopicChanged={setTopicChanged}
          isCombinedView={true}
        />
      )}
    </div>
  );
};

export default CombinedView;
